/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { useTranslation } from "react-i18next";
import ROUTES from "routes";
import { useUser } from "state/UserContext";

import { Button, HR, Heading, Stack, Text, PlusIcon } from "components";

import { FC } from "react";

type Props = {
  canAddFlight: boolean;
  canAddAccommodation: boolean;
};

const RequireAttention: FC<Props> = ({ canAddFlight, canAddAccommodation }) => {
  const { t } = useTranslation();
  const { userData } = useUser();

  return canAddFlight || canAddAccommodation ? (
    <Stack className="w-full -rounded-outer-container">
      {canAddFlight && (
        <Stack>
          <div className="flex flex-col md:flex-row space-y-2.5 md:space-y-s0 md:gap-x-5 justify-between md:items-center">
            <Stack className="space-y-1">
              <Heading as="h2" className="-text--theme-colors-text-title-brand">
                {t("requireAttention.flight")}
              </Heading>

              <Text className="text-sm md:text-base -text--theme-colors-text-body">
                {t("requireAttention.flightDetails")}
              </Text>
            </Stack>

            <div className="hidden md:flex">
              <Button
                as="link"
                to={ROUTES.dashboard.generalFlight(userData?.hash_id!, "new")}
                variant="primary"
                className="h-12 w-36"
              >
                <div className="flex gap-x-2.5 items-center justify-center">
                  <PlusIcon />
                  <Text>{t("common.buttons.add")}</Text>
                </div>
              </Button>
            </div>
          </div>

          <div className="flex md:hidden mt-2.5 w-full">
            <Button
              as="link"
              to={ROUTES.dashboard.generalFlight(userData?.hash_id!, "new")}
              variant="primary"
              className="h-12 w-36"
            >
              <div className="flex gap-x-2.5 items-center justify-center">
                <PlusIcon />
                <Text>{t("requireAttention.add")}</Text>
              </div>
            </Button>
          </div>
        </Stack>
      )}

      {canAddFlight && canAddAccommodation && <HR />}

      {canAddAccommodation && (
        <Stack>
          <div className="flex">
            <Stack className="space-y-1">
              <Heading as="h2" className="-text--theme-colors-text-title-brand">
                {t("requireAttention.hotel")}
              </Heading>

              <Text className="text-sm md:text-base -text--theme-colors-text-body">
                {t("requireAttention.hotelDetails")}
              </Text>
            </Stack>

            <div className="hidden md:flex">
              <Button
                to={ROUTES.dashboard.generalReservation(
                  userData?.hash_id!,
                  "new"
                )}
                as="link"
                variant="primary"
                className="h-12 w-36"
              >
                <div className="flex gap-x-2.5 items-center justify-center">
                  <PlusIcon />
                  <Text className="whitespace-nowrap">
                    {t("common.buttons.add")}
                  </Text>
                </div>
              </Button>
            </div>
          </div>

          <div className="flex md:hidden mt-2.5 w-full">
            <Button
              to={ROUTES.dashboard.generalReservation(
                userData?.hash_id!,
                "new"
              )}
              as="link"
              variant="primary"
              className="h-12 w-36"
            >
              <div className="flex gap-x-2.5 items-center justify-center">
                <PlusIcon />
                <Text>{t("requireAttention.add")}</Text>
              </div>
            </Button>
          </div>
        </Stack>
      )}
    </Stack>
  ) : null;
};

export default RequireAttention;
